import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import { FileCheck, Search, Trash2, ChevronDown, Download, ArrowUpDown, ShieldCheck } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const MyVerifiedLOIsPage = () => {
  const { user } = useAuth();
  const { userTheme } = useTheme();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const limit = 20;

  const pageStyles = userTheme?.page || {};
  const requestsBox = userTheme?.requestsBox || {};
  const buttonStyles = userTheme?.buttons || {};
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';

  const isAdmin = user?.role === 'main_admin' || user?.role === 'company_admin';

  const fetchVerifications = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit });
      if (search) params.append('search', search);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);
      if (gradeFilter !== 'all') params.append('grade', gradeFilter);

      const res = await api.get(`/loi-verification/my-verifications?${params}`);
      setVerifications(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to load verifications');
    } finally {
      setLoading(false);
    }
  }, [page, search, sortBy, sortOrder, gradeFilter]);

  useEffect(() => {
    fetchVerifications();
  }, [fetchVerifications]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/loi-verification/my-verifications/${deleteModal.id}`);
      toast.success('Verification deleted');
      setDeleteModal(null);
      fetchVerifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const getGradeBadge = (grade) => {
    const colors = {
      'A+': { bg: '#D1FAE5', text: '#065F46' },
      'A': { bg: '#D1FAE5', text: '#065F46' },
      'B': { bg: '#DBEAFE', text: '#1E40AF' },
      'C': { bg: '#FEF3C7', text: '#92400E' },
      'D': { bg: '#FEE2E2', text: '#991B1B' },
      'F': { bg: '#FEE2E2', text: '#991B1B' },
    };
    const style = colors[grade] || colors['C'];
    return (
      <span
        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold"
        style={{ backgroundColor: style.bg, color: style.text }}
      >
        {grade}
      </span>
    );
  };

  const handleDownloadReport = async (v) => {
    if (!v.id) return;
    try {
      const response = await api.get(`/loi-verification/report/${v.id}`, {
        responseType: 'blob',
        timeout: 60000,
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let filename = v.original_filename || 'report.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?([^;\n]+)/i);
        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1].replace(/['"]/g, ''));
        }
      }
      if (!filename.toLowerCase().endsWith('.pdf')) {
        filename = filename.replace(/\.[^.]+$/, '') + '_report.pdf';
      } else {
        filename = filename.replace(/\.pdf$/i, '_report.pdf');
      }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && verifications.length === 0) {
    return (
      <Sidebar>
        <div
          className="min-h-screen flex items-center justify-center"
          style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
        >
          <div
            className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300"
            style={{ borderTopColor: primaryColor }}
          ></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div
        className="p-8 min-h-screen"
        style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1
              className="text-2xl font-semibold mb-2"
              style={{ color: requestsBox.titleColor || '#111827' }}
            >
              {isAdmin ? 'All LOIs Verifications' : 'My Verified LOIs'}
            </h1>
            <p style={{ color: requestsBox.textColor || '#6B7280' }}>
              {isAdmin
                ? 'View all LOI verifications across the platform'
                : 'View your LOI verification history'}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by filename or email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 w-64"
                style={{ borderColor: requestsBox.borderColor || '#E5E7EB' }}
              />
            </div>
            <button
              type="submit"
              className="px-3 py-2 rounded-lg text-sm text-white font-medium hover:opacity-90 transition"
              style={{ backgroundColor: primaryColor }}
            >
              Search
            </button>
          </form>

          {/* Grade filter */}
          <div className="relative">
            <select
              value={gradeFilter}
              onChange={(e) => { setGradeFilter(e.target.value); setPage(1); }}
              className="appearance-none pl-3 pr-8 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2"
              style={{ borderColor: requestsBox.borderColor || '#E5E7EB' }}
            >
              <option value="all">All Grades</option>
              <option value="A+">A+</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
              <option value="D">D</option>
              <option value="F">F</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          <span className="text-sm" style={{ color: requestsBox.textColor || '#6B7280' }}>
            {total} verification{total !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Table */}
        {verifications.length === 0 && !loading ? (
          <div
            className="rounded-xl p-12 text-center"
            style={{
              backgroundColor: requestsBox.backgroundColor || '#FFFFFF',
              border: `1px solid ${requestsBox.borderColor || '#E5E7EB'}`,
            }}
          >
            <ShieldCheck className="w-16 h-16 mx-auto mb-4" style={{ color: requestsBox.borderColor || '#D1D5DB' }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: requestsBox.titleColor || '#4B5563' }}>
              {search || gradeFilter !== 'all' ? 'No verifications match your filters' : 'No verified LOIs yet'}
            </h3>
            <p style={{ color: requestsBox.textColor || '#6B7280' }}>
              {search || gradeFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Your LOI verifications will appear here once you verify a document.'}
            </p>
          </div>
        ) : (
          <>
            <div
              className="rounded-xl shadow-sm overflow-hidden"
              style={{
                backgroundColor: requestsBox.backgroundColor || '#FFFFFF',
                border: `1px solid ${requestsBox.borderColor || '#E5E7EB'}`,
              }}
            >
              <table className="w-full">
                <thead style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB', borderBottom: `1px solid ${requestsBox.borderColor || '#E5E7EB'}` }}>
                  <tr>
                    <th
                      className="text-left px-6 py-3 text-sm font-medium cursor-pointer select-none"
                      style={{ color: requestsBox.textColor || '#6B7280' }}
                      onClick={() => toggleSort('created_at')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Date
                        <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                    <th
                      className="text-left px-6 py-3 text-sm font-medium cursor-pointer select-none"
                      style={{ color: requestsBox.textColor || '#6B7280' }}
                      onClick={() => toggleSort('original_filename')}
                    >
                      <span className="inline-flex items-center gap-1">
                        File Name
                        <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                    {isAdmin && (
                      <th className="text-left px-6 py-3 text-sm font-medium" style={{ color: requestsBox.textColor || '#6B7280' }}>
                        Uploaded By
                      </th>
                    )}
                    <th
                      className="text-left px-6 py-3 text-sm font-medium cursor-pointer select-none"
                      style={{ color: requestsBox.textColor || '#6B7280' }}
                      onClick={() => toggleSort('score')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Score
                        <ArrowUpDown className="w-3 h-3" />
                      </span>
                    </th>
                    <th className="text-left px-6 py-3 text-sm font-medium" style={{ color: requestsBox.textColor || '#6B7280' }}>
                      Grade
                    </th>
                    <th className="text-right px-6 py-3 text-sm font-medium" style={{ color: requestsBox.textColor || '#6B7280' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {verifications.map((v, idx) => {
                    const summary = v.result?.summary || {};
                    return (
                      <tr
                        key={v.id}
                        className="hover:bg-gray-50"
                        style={{ borderTop: idx > 0 ? `1px solid ${requestsBox.borderColor || '#E5E7EB'}` : 'none' }}
                      >
                        <td className="px-6 py-4">
                          <span className="text-sm" style={{ color: requestsBox.titleColor || '#111827' }}>
                            {formatDate(v.created_at)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium" style={{ color: primaryColor }}>
                            {v.original_filename || 'Unknown'}
                          </span>
                        </td>
                        {isAdmin && (
                          <td className="px-6 py-4">
                            <span className="text-sm" style={{ color: requestsBox.textColor || '#6B7280' }}>
                              {v.uploader_email || v.uploader_name || '-'}
                            </span>
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium" style={{ color: requestsBox.titleColor || '#111827' }}>
                            {summary.score != null ? `${summary.score}%` : '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {summary.grade ? getGradeBadge(summary.grade) : '-'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => handleDownloadReport(v)}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition"
                              style={{ color: primaryColor }}
                              title="Download Report"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteModal(v)}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-red-50 transition text-red-500"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm" style={{ color: requestsBox.textColor || '#6B7280' }}>
                  Page {page} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 rounded-lg text-sm border disabled:opacity-50 hover:bg-gray-50 transition"
                    style={{ borderColor: requestsBox.borderColor || '#E5E7EB', color: requestsBox.titleColor || '#111827' }}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 rounded-lg text-sm border disabled:opacity-50 hover:bg-gray-50 transition"
                    style={{ borderColor: requestsBox.borderColor || '#E5E7EB', color: requestsBox.titleColor || '#111827' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-semibold mb-2 text-gray-900">Delete Verification</h3>
              <p className="text-sm text-gray-600 mb-1">
                Are you sure you want to delete this verification?
              </p>
              <p className="text-sm font-medium text-gray-800 mb-4">
                {deleteModal.original_filename || 'Unknown file'}
              </p>
              <p className="text-xs text-red-500 mb-6">
                This action cannot be undone. The verification report will be permanently removed.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteModal(null)}
                  className="px-4 py-2 rounded-lg text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 transition"
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default MyVerifiedLOIsPage;
